import { useEffect, useRef } from "react";
import {
  RiArrowRightSLine,
  RiBarcodeLine,
  RiFolder3Line,
  RiFolderOpenLine,
  RiPriceTag3Line,
} from "react-icons/ri";
import { CheckState, RollupStats } from "./treeModel";
import { StateChips } from "./TreeChips";
import TreeRowMenu, { RowMenuItem } from "./TreeRowMenu";
import TreeIndent from "./TreeIndent";

export type FolderKind = "barcode" | "matcode" | "section";

export interface TreeFolderRowProps {
  kind: FolderKind;
  /** Tree node id — also the DOM handle the tree uses to move focus. */
  rowId: string;
  /** Folder name exactly as it appears in the drop, without the trailing slash. */
  name: string;
  /** Full folder path, so two `cover/` folders never share an accessible name. */
  path: string;
  /** The product name, on a matcode row only — no other level has one in the data. */
  subtitle?: string;
  level: number;
  posinset: number;
  setsize: number;
  stats: RollupStats;
  expanded: boolean;
  hasChildren: boolean;
  checkState: CheckState;
  focused: boolean;
  menuItems: RowMenuItem[];
  onToggleExpand(): void;
  onToggleCheck(): void;
  onFocusRow(): void;
}

const ICONS: Record<FolderKind, (open: boolean) => JSX.Element> = {
  barcode: () => <RiBarcodeLine size={15} className="text-gray-500" aria-hidden="true" />,
  matcode: () => <RiPriceTag3Line size={15} className="text-gray-500" aria-hidden="true" />,
  section: (open) =>
    open ? (
      <RiFolderOpenLine size={15} className="text-gray-500" aria-hidden="true" />
    ) : (
      <RiFolder3Line size={15} className="text-gray-500" aria-hidden="true" />
    ),
};

const NAME_STYLE: Record<FolderKind, string> = {
  barcode: "text-sm font-bold text-gray-800 tabular-nums",
  matcode: "text-sm font-semibold text-gray-800 tabular-nums",
  section: "text-sm font-medium text-gray-600",
};

export default function TreeFolderRow({
  kind,
  rowId,
  name,
  path,
  subtitle,
  level,
  posinset,
  setsize,
  stats,
  expanded,
  hasChildren,
  checkState,
  focused,
  menuItems,
  onToggleExpand,
  onToggleCheck,
  onFocusRow,
}: TreeFolderRowProps) {
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = checkState === "indeterminate";
  }, [checkState]);

  const empty = stats.photos === 0;

  return (
    <div
      role="treeitem"
      data-row-id={rowId}
      aria-level={level}
      aria-posinset={posinset}
      aria-setsize={setsize}
      aria-expanded={hasChildren ? expanded : undefined}
      // A folder counts as selected only when every photo beneath it is; the checkbox
      // carries the partial case, which treeitem has no ARIA state for.
      aria-selected={checkState === "checked"}
      aria-label={`${path}/`}
      tabIndex={focused ? 0 : -1}
      onFocus={onFocusRow}
      className="group flex items-stretch transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
    >
      <TreeIndent level={level} />

      <button
        type="button"
        onClick={onToggleExpand}
        tabIndex={-1}
        aria-hidden="true"
        disabled={!hasChildren}
        className="flex w-5 shrink-0 items-start justify-center pt-2 text-gray-400 transition-colors hover:text-gray-700 disabled:opacity-0 sm:items-center sm:pt-0"
      >
        <RiArrowRightSLine
          size={16}
          className={`transition-transform duration-150 ${expanded ? "rotate-90" : ""}`}
        />
      </button>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 py-1.5 pr-1 sm:flex-nowrap">
        <span className="-m-2 flex shrink-0 p-2">
          <input
            ref={box}
            type="checkbox"
            checked={checkState === "checked"}
            onChange={onToggleCheck}
            disabled={empty}
            tabIndex={focused ? 0 : -1}
            aria-label={`เลือกทุกรูปใน ${path}/`}
            className="h-3.5 w-3.5 cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          />
        </span>

        {ICONS[kind](expanded)}

        <button
          type="button"
          onClick={onToggleExpand}
          tabIndex={-1}
          className="flex min-w-0 items-baseline gap-2 text-left"
        >
          <span className={`${NAME_STYLE[kind]} shrink-0`}>
            {name}
            <span className="text-gray-400">/</span>
          </span>
          {subtitle && (
            <span className="min-w-0 truncate text-xs leading-5 text-gray-600">{subtitle}</span>
          )}
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {empty ? (
            <span className="text-xs leading-5 text-gray-400">ว่าง</span>
          ) : (
            <StateChips stats={stats} />
          )}
        </div>

        <TreeRowMenu items={menuItems} label={`ตัวเลือกของ ${path}/`} tabIndex={focused ? 0 : -1} />
      </div>
    </div>
  );
}
